/**
 * Pure clinic-assignment logic for the provider form (DoctorsList slideout).
 *
 * Extracted so the #294 invariant is unit-testable: the form only RENDERS
 * rows for clinics the viewer can name (admins: active clinics; managers:
 * their own), but a provider may hold assignments beyond that set — archived
 * clinics, or other clinics for a manager. Those hidden assignments must
 * survive every edit and every save untouched: toggling a visible clinic, or
 * submitting the form, may never drop an id the form never displayed.
 */

import { normalizeProviderClinicFields, type ProviderClinicFields } from '../../shared/providerClinics'

export type AssignmentDraft = ProviderClinicFields

/** Toggle one clinic's assignment. Unassigning also clears its default claim.
 *  Ids other than `id` — including hidden/archived ones — pass through as-is. */
export function toggleClinicAssignment(draft: AssignmentDraft, id: string): AssignmentDraft {
  const assigned = draft.clinicIds.includes(id)
  return {
    clinicIds: assigned ? draft.clinicIds.filter(c => c !== id) : [...draft.clinicIds, id],
    defaultForClinicIds: assigned
      ? draft.defaultForClinicIds.filter(c => c !== id)
      : draft.defaultForClinicIds,
  }
}

/** Toggle one clinic's default-provider claim; assignments are untouched. */
export function toggleDefaultClinic(draft: AssignmentDraft, id: string): AssignmentDraft {
  return {
    ...draft,
    defaultForClinicIds: draft.defaultForClinicIds.includes(id)
      ? draft.defaultForClinicIds.filter(c => c !== id)
      : [...draft.defaultForClinicIds, id],
  }
}

/** What a save submits: every assigned clinic (hidden ones included), with
 *  defaults constrained to assigned clinics — so a default at a since-archived
 *  clinic survives exactly as long as its assignment does. The subset rule is
 *  the SHARED one (providerClinics.ts — the same normalization every reader
 *  and firestore.rules applies), not a local copy that could drift from it. */
export function submittedAssignments(draft: AssignmentDraft): AssignmentDraft {
  return normalizeProviderClinicFields(draft)
}

/** Assigned clinic ids the form can't render a row for (not in the visible
 *  clinics map): archived clinics for an admin, other clinics for a manager.
 *  Takes any membership probe (a Map of clinics qualifies) — no copying. */
export function unlistedAssignedCount(
  clinicIds: string[],
  visibleClinicIds: { has(id: string): boolean },
): number {
  return clinicIds.filter(id => !visibleClinicIds.has(id)).length
}
